import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';

import {
  isLifetime,
  useBenefitDesignDraftFilters,
  MAX_INFLATION_PERCENT,
  type BenefitDesignAvailableOptions,
  type BenefitPaymentTypeFilter,
} from '@/features/shop-insurance';

interface BenefitDesignFiltersProps {
  availableOptions: BenefitDesignAvailableOptions;
  availableCarriers?: string[];
  onMobileClose?: () => void;
}

export function BenefitDesignFilters({ availableCarriers = [], onMobileClose }: BenefitDesignFiltersProps) {
  const {
    draft,
    useMinimumInflation,
    setField,
    setDurationType,
    setInflationMode,
    setInflationTypeFilter,
    toggleDraftCarrier,
    setDraftBenefitPaymentType,
    handleApply,
    handleReset,
  } = useBenefitDesignDraftFilters(availableCarriers);

  return (
    <div className="space-y-4">
      {/* Monthly Benefit */}
      <div className="space-y-2">
        <Label className="text-primary-dark text-sm font-semibold">Monthly Benefit</Label>
        <div className="relative">
          <span className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 text-sm">
            $
          </span>
          <Input
            type="number"
            min={1}
            className="pl-6"
            value={draft.benefit}
            onChange={event => setField('benefit', event.target.value)}
            onWheel={e => e.currentTarget.blur()}
            placeholder="e.g. 6000"
            data-testid="benefit-design-filters-monthly-benefit"
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Desired LTC coverage per month. Carriers outside their premium range will be excluded.
        </p>
      </div>

      {/* Carriers */}
      {availableCarriers.length > 0 && (
        <div className="border-border space-y-2 border-t pt-4">
          <Label className="text-primary-dark text-sm font-semibold">Carriers</Label>
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full focus-visible:outline-none">
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                data-testid="benefit-design-filters-carriers-dropdown"
              >
                <span className="truncate">
                  {(() => {
                    const relevantHidden = draft.draftHiddenCarriers.filter(c => availableCarriers.includes(c));
                    return relevantHidden.length === 0
                      ? 'All Carriers'
                      : `${availableCarriers.length - relevantHidden.length} of ${availableCarriers.length} Selected`;
                  })()}
                </span>
                <svg className="size-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width)" align="start">
              {availableCarriers.map(carrier => (
                <DropdownMenuCheckboxItem
                  key={carrier}
                  checked={!draft.draftHiddenCarriers.includes(carrier)}
                  onCheckedChange={() => toggleDraftCarrier(carrier)}
                  onSelect={e => e.preventDefault()}
                  data-testid={`benefit-design-filters-carrier-${carrier}`}
                >
                  {carrier}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Benefit Type */}
      <div className="border-border space-y-2 border-t pt-4">
        <Label className="text-primary-dark text-sm font-semibold">Benefit Type</Label>
        <Select
          value={draft.draftBenefitPaymentType}
          onValueChange={value => setDraftBenefitPaymentType(value as BenefitPaymentTypeFilter)}
        >
          <SelectTrigger
            className="w-full"
            data-testid="benefit-design-filters-benefit-type"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="reimbursement">Reimbursement</SelectItem>
            <SelectItem value="indemnity">Indemnity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Benefit Duration */}
      <div className="border-border space-y-3 border-t pt-4">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-primary-dark text-sm font-semibold">Benefit Duration</Label>
          <Select
            value={draft.durationType}
            onValueChange={value => setDurationType(value as 'combined' | 'separate')}
          >
            <SelectTrigger
              size="sm"
              className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5"
              data-testid="benefit-design-filters-duration-mode"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="combined">Total Duration</SelectItem>
              <SelectItem value="separate">Separate AOB/COB</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {draft.durationType === 'combined' ? (
          <div className="space-y-1.5">
            <div className="relative">
              <Input
                type="text"
                inputMode="text"
                value={draft.duration}
                onChange={event => setField('duration', event.target.value)}
                placeholder='e.g. 3 or "Lifetime"'
                data-testid="benefit-design-filters-duration"
              />
              {!isLifetime(draft.duration) && (
                <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-sm">
                  yrs
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-xs">
              1-8 years, or type "Lifetime" for unlimited coverage.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">AOB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={9}
                    value={draft.aobDuration}
                    onChange={event => setField('aobDuration', event.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="yrs"
                    data-testid="benefit-design-filters-duration-aob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    yrs
                  </span>
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">COB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={9}
                    value={draft.cobDuration}
                    onChange={event => setField('cobDuration', event.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="yrs"
                    data-testid="benefit-design-filters-duration-cob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    yrs
                  </span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">AOB and COB durations each 1–8 years.</p>
          </div>
        )}
      </div>

      {/* Inflation Protection */}
      <div className="border-border space-y-3 border-t pt-4">
        {useMinimumInflation ? (
          <Label className="text-primary-dark text-sm font-semibold">Inflation Protection</Label>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <Label className="text-primary-dark text-sm font-semibold">Inflation Protection</Label>
            <Select
              value={draft.inflationType}
              onValueChange={value => setInflationMode(value as 'combined' | 'separate')}
            >
              <SelectTrigger
                size="sm"
                className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5"
                data-testid="benefit-design-filters-inflation-mode"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Combined</SelectItem>
                <SelectItem value="separate">Separate AOB/COB</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {useMinimumInflation || draft.inflationType === 'combined' ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  max={MAX_INFLATION_PERCENT}
                  value={draft.inflation}
                  onChange={event => {
                    const val = event.target.value;
                    if (val !== '' && Number(val) > MAX_INFLATION_PERCENT) return;
                    setField('inflation', val);
                  }}
                  onWheel={e => e.currentTarget.blur()}
                  placeholder="% (e.g. 3)"
                  data-testid="benefit-design-filters-inflation"
                />
                <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-sm">
                  %
                </span>
              </div>
              <p className="text-muted-foreground text-xs">
                {useMinimumInflation
                  ? 'Minimum protection. A policy qualifies if its AOB or COB rate reaches this percentage.'
                  : '0–5%. Common values: 0%, 3%, 5%.'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">Inflation Type</Label>
              <Select
                value={draft.inflationTypeFilter}
                onValueChange={value =>
                  setInflationTypeFilter(value as 'all' | 'simple' | 'compound')
                }
              >
                <SelectTrigger
                  className="w-full"
                  data-testid="benefit-design-filters-inflation-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="simple">Simple</SelectItem>
                  <SelectItem value="compound">Compound</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">AOB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={0}
                    max={MAX_INFLATION_PERCENT}
                    value={draft.aobInflation}
                    onChange={event => {
                      const val = event.target.value;
                      if (val !== '' && Number(val) > MAX_INFLATION_PERCENT) return;
                      setField('aobInflation', val);
                    }}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="%"
                    data-testid="benefit-design-filters-inflation-aob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    %
                  </span>
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">COB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={0}
                    max={MAX_INFLATION_PERCENT}
                    value={draft.cobInflation}
                    onChange={event => {
                      const val = event.target.value;
                      if (val !== '' && Number(val) > MAX_INFLATION_PERCENT) return;
                      setField('cobInflation', val);
                    }}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="%"
                    data-testid="benefit-design-filters-inflation-cob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    %
                  </span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Enter AOB and COB inflation rates separately (0–5%).
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-border flex flex-col gap-2 border-t pt-4">
        <Button
          onClick={() => { handleApply(); onMobileClose?.(); }}
          data-testid="benefit-design-filters-apply"
        >
          Apply
        </Button>
        <Button
          variant="outline"
          onClick={() => { handleReset(); onMobileClose?.(); }}
          data-testid="benefit-design-filters-reset"
        >
          Reset
        </Button>
      </div>
    </div>
  );
}
